import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { feelingName, RAINBOW_ORDER, SECTIONS } from "../../lib/sections";
import { FEEL, MEMORY_STAND, WINDOWS } from "../../lib/world";
import { hallPrompt, HALL_GUIDE_LINE } from "../../lib/copy";
import { glide, glideTo, portal, pose } from "./Player";

// The visit's storyline: rooms in rainbow order, then the Living Windows, then the Memory Wall.
export const TOUR = [...RAINBOW_ORDER, "windows", "memory"];
const KEY = "afae-visited";
export const visitedSet = () => new Set(JSON.parse(sessionStorage.getItem(KEY) || "[]"));
export function markVisited(id) {
  const v = visitedSet();
  if (!TOUR.includes(id) || v.has(id)) return;
  v.add(id);
  sessionStorage.setItem(KEY, JSON.stringify([...v]));
  window.dispatchEvent(new Event("afae-visited"));
}
export const nextPlace = (v = visitedSet()) => TOUR.find((id) => !v.has(id)) || null;
export const placeName = (id) => SECTIONS[id]?.name || (id === "windows" ? "the Living Windows" : id === "memory" ? "the Memory Wall" : id === "flipbook" ? "The Flipbook" : "the Feeling Wall");
export const placePoint = (id) => (id === "windows" ? { x: WINDOWS.cx, z: WINDOWS.z - 2.2 } : id === "memory" ? MEMORY_STAND : { x: FEEL.xs[id], z: FEEL.z + 1.6 });

export function useVisited() {
  const [v, setV] = useState(visitedSet);
  useEffect(() => {
    const on = () => setV(visitedSet());
    window.addEventListener("afae-visited", on);
    return () => window.removeEventListener("afae-visited", on);
  }, []);
  return v;
}

const pill = "rounded-full border border-[#E2B66B]/70 bg-[#FFF9EE]/90 font-display italic text-[#4A2D55] shadow-[0_0_26px_rgba(255,200,110,0.55)] transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-white";

export function NextChip({ visited, onTour }) {
  const next = nextPlace(visited);
  const [prompt] = useState(hallPrompt);
  const label = !next ? "Leave the gallery ›" : SECTIONS[next] ? `Next ${feelingName(next)} ›` : next === "windows" ? "Visit the Living Windows ›" : "Sit a while at the Memory Wall ›";
  return (
    <div className="pointer-events-none fixed bottom-[calc(env(safe-area-inset-bottom)+9rem)] left-[4.5rem] z-[38] flex max-w-[calc(100vw-5.5rem)] flex-col items-start gap-1.5 sm:bottom-[calc(env(safe-area-inset-bottom)+4.75rem)] sm:left-[5.5rem] [.walkpad_&]:bottom-[calc(env(safe-area-inset-bottom)+11.25rem)]">
      {visited.size ? (
        <motion.button data-testid="next-chip" data-next={next || "leave"} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} onClick={() => (next ? glideTo(next) : window.dispatchEvent(new CustomEvent("afae-leave")))} className={`pointer-events-auto px-5 py-1.5 text-lg ${pill}`}>
          {label}
        </motion.button>
      ) : (
        <motion.p data-testid="hall-prompt" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className={`px-5 py-1.5 text-base sm:text-lg ${pill} hover:translate-y-0 hover:bg-[#FFF9EE]/90`}>
          {prompt}
        </motion.p>
      )}
      <button data-testid="tour-start-button" onClick={onTour} className="pointer-events-auto rounded-full bg-[#FFF9EE]/70 px-3 py-0.5 font-display text-sm italic text-[#4A2D55] underline-offset-4 hover:underline">
        {HALL_GUIDE_LINE}
      </button>
    </div>
  );
}

const MAP_XY = { fire: [44, 34], spirit: [102, 26], nature: [160, 22], thoughts: [218, 26], love: [276, 34], windows: [250, 206], memory: [160, 214] };
export function MapPanel({ open, onClose, region, visited }) {
  const here = region !== "hall" ? MAP_XY[region] : [160 + pose.x * 11.5, 136 + pose.z * 9.5];
  const go = (id) => {
    onClose();
    glideTo(id);
  };
  return (
    <AnimatePresence>
      {open && (
        <motion.div data-testid="map-panel" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] grid place-items-center bg-[#2D1E3E]/30 p-4 backdrop-blur-sm" onClick={onClose}>
          <motion.div initial={{ y: 14, scale: 0.97 }} animate={{ y: 0, scale: 1 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-[28px] border border-[#E2B66B]/50 bg-[#FFF9F0] p-5 shadow-[0_30px_80px_-30px_rgba(74,45,85,0.6)]">
            <div className="mb-2 flex items-baseline justify-between">
              <h2 className="font-display text-2xl italic text-[#4A2D55]">The gallery</h2>
              <button data-testid="map-close" onClick={onClose} className="font-display text-base italic text-[#7E57C2]">close</button>
            </div>
            <svg viewBox="0 0 320 240" className="w-full" data-testid="map-svg">
              <defs>
                <filter id="wash"><feTurbulence baseFrequency="0.04" numOctaves="2" seed="3" /><feDisplacementMap in="SourceGraphic" scale="5" /></filter>
              </defs>
              <rect x="20" y="62" width="280" height="160" rx="18" fill="#F6EBDD" stroke="#D9C2A4" strokeWidth="2" filter="url(#wash)" />
              <rect x="20" y="62" width="280" height="10" fill="#E9D6F0" />
              <text x="160" y="88" textAnchor="middle" fontFamily="Cormorant Garamond" fontStyle="italic" fontSize="13" fill="#4A2D55">the Feeling Wall</text>
              {TOUR.map((id) => {
                const [x, y] = MAP_XY[id];
                const col = SECTIONS[id]?.tint || (id === "windows" ? "#F2C46B" : "#7E6A9E");
                return (
                  <g key={id} data-testid={`map-place-${id}`} data-visited={visited.has(id) ? "1" : "0"} onClick={() => go(id)} className="cursor-pointer">
                    <circle cx={x} cy={y} r="17" fill={col} opacity="0.35" filter="url(#wash)" />
                    <text x={x} y={y + 4} textAnchor="middle" fontFamily="Cormorant Garamond" fontSize="11" fill="#2D1E3E">{SECTIONS[id]?.name || (id === "windows" ? "Windows" : "Memory")}</text>
                    {visited.has(id) && <text x={x + 14} y={y - 10} fontSize="12" fill="#3E8E5E">✓</text>}
                  </g>
                );
              })}
              <g data-testid="map-place-flipbook" onClick={() => go("flipbook")} className="cursor-pointer">
                <rect x="88" y="200" width="44" height="26" rx="5" fill="#C98A5A" opacity="0.35" filter="url(#wash)" />
                <path d="M96 206 l9 3 l9 -3 v12 l-9 3 l-9 -3 z M105 209 v12" fill="#FFF8EE" stroke="#8A5A3A" strokeWidth="0.8" />
                <text x="110" y="236" textAnchor="middle" fontFamily="Cormorant Garamond" fontSize="10" fill="#2D1E3E">Flipbook</text>
              </g>
              <g data-testid="map-place-hall" onClick={() => go("hall")} className="cursor-pointer">
                <rect x="110" y="120" width="100" height="34" rx="12" fill="transparent" />
              </g>
              <g data-testid="map-here" transform={`translate(${here[0]},${here[1]})`}>
                <path d="M0 -9 L6 4 L0 1 L-6 4 Z" fill="#C2185B" />
                <text y="18" textAnchor="middle" fontSize="9" fontFamily="Cormorant Garamond" fontStyle="italic" fill="#C2185B">you are here</text>
              </g>
            </svg>
            <p className="mt-1 text-center font-display text-sm italic text-[#5C4A72]">Tap a place to glide there.</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function MapButton({ onClick }) {
  return (
    <button data-testid="map-button" aria-label="Map" onClick={onClick} className="fixed bottom-24 left-4 z-[40] grid h-11 w-11 place-items-center rounded-full border border-[#B39DDB]/60 bg-white/70 text-[#4A2D55] transition-transform duration-200 hover:-translate-y-0.5 sm:bottom-20 sm:left-6 [.walkpad_&]:bottom-[calc(env(safe-area-inset-bottom)+9.5rem)] [.walkpad_&]:left-auto [.walkpad_&]:right-4">
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2z" /><path d="M9 4v14M15 6v14" /></svg>
    </button>
  );
}

// Guided tour: each room one painting at a time (~8s each once the glide settles), its finale, then the windows and the Memory Wall.
export function useTour({ region, atEnd, stepTo, paused }) {
  const [tour, setTour] = useState({ on: false, playing: false, i: 0, byTap: false, done: false });
  useEffect(() => void window.dispatchEvent(new CustomEvent("afae-tourend", { detail: tour.done })), [tour.done]);
  const live = useRef({});
  live.current = { region, atEnd, stepTo };
  const r = useRef({ t: 0, sent: null, endT: 0 });
  const advance = () => {
    r.current = { t: 0, sent: null, endT: 0 };
    setTour((t) => (t.i + 1 >= TOUR.length ? { on: false, playing: false, i: 0, byTap: false, done: true } : { ...t, i: t.i + 1 }));
  };
  const api = {
    tour,
    start: () => {
      r.current = { t: 0, sent: null, endT: 0 };
      setTour({ on: true, playing: true, i: 0, byTap: false, done: false });
    },
    toggle: () => setTour((t) => ({ ...t, playing: !t.playing, byTap: false })),
    end: () => setTour({ on: false, playing: false, i: 0, byTap: false, done: false }),
    closeDone: () => setTour((t) => ({ ...t, done: false })),
    next: () => {
      const place = TOUR[tour.i];
      if (live.current.region === place && !live.current.atEnd) {
        r.current.t = 0;
        live.current.stepTo(1);
      } else advance();
    },
    pauseForTap: () => setTour((t) => (t.on && t.playing ? { ...t, playing: false, byTap: true } : t)),
  };
  useEffect(() => {
    const on = () => api.start();
    window.addEventListener("afae-tour", on);
    return () => window.removeEventListener("afae-tour", on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const wasPaused = useRef(paused);
  useEffect(() => {
    if (wasPaused.current && !paused && tour.byTap) setTour((t) => ({ ...t, playing: true, byTap: false }));
    wasPaused.current = paused;
  }, [paused, tour.byTap]);
  useEffect(() => {
    window.__afaeTour = { ...tour, place: TOUR[tour.i], t: r.current.t };
    if (!tour.on || !tour.playing || paused) return;
    const id = setInterval(() => {
      const s = r.current;
      const { region: reg, atEnd: end, stepTo: st } = live.current;
      const place = TOUR[tour.i];
      if (glide.on || portal.on) return void (s.t = 0);
      if (s.sent !== place) {
        s.sent = place;
        s.t = 0;
        if (reg !== place) return glideTo(place);
      }
      s.t += 0.25;
      window.__afaeTour = { ...tour, place, t: s.t };
      if (SECTIONS[place]) {
        if (reg !== place) return;
        if (end) return (s.endT += 0.25) > 2.5 && advance();
        if (s.t >= 8) {
          s.t = 0;
          st(1);
        }
      } else if (s.t >= 10) advance();
    }, 250);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour.on, tour.playing, tour.i, paused]);
  return api;
}

export function TourBar({ api }) {
  const { tour } = api;
  return (
    <AnimatePresence>
      {tour.on && (
        <motion.div data-testid="tour-bar" data-playing={tour.playing ? "1" : "0"} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className={`fixed left-1/2 top-[4.5rem] z-[45] flex -translate-x-1/2 items-center gap-1 px-2 py-1 sm:top-20 ${pill}`}>
          <span className="px-2 text-sm not-italic text-[#7E57C2]">tour · {placeName(TOUR[tour.i])}</span>
          <button data-testid="tour-pause" onClick={api.toggle} className="rounded-full px-3 py-1 text-base hover:bg-[#F3E8FF]">{tour.playing ? "pause" : "play"}</button>
          <button data-testid="tour-next" onClick={api.next} className="rounded-full px-3 py-1 text-base hover:bg-[#F3E8FF]">next ›</button>
          <button data-testid="tour-end" onClick={api.end} className="rounded-full px-3 py-1 text-base hover:bg-[#F3E8FF]">end tour</button>
        </motion.div>
      )}
      {tour.done && (
        <motion.div data-testid="tour-end-panel" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-x-0 bottom-28 z-[45] mx-auto flex w-fit flex-col items-center gap-3 rounded-[24px] bg-[#FFF9F0]/95 px-6 py-4 shadow-xl">
          <p className="font-display text-xl italic text-[#4A2D55]">That was the whole gallery. Thank you for walking with me.</p>
          <div className="flex gap-3">
            <button data-testid="tour-leave" onClick={() => (api.closeDone(), window.dispatchEvent(new CustomEvent("afae-leave")))} className={`px-4 py-1 ${pill}`}>Leave the gallery</button>
            <button data-testid="tour-wander" onClick={api.closeDone} className={`px-4 py-1 ${pill}`}>keep wandering</button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
